import { useSelector, useDispatch } from "react-redux"
import { RootState } from "../store"
import {
  setLoading,
  setAccountData,
  setError,
  clearAccount,
  setConfigurations,
} from "./store"
import { AccountService } from "../../services/account.service"
import { useCallback } from "react"
import {
  ConfigurationData,
  ConfigurationService,
} from "../../services/configuration.service"

export const useAccount = () => {
  const dispatch = useDispatch()
  const accountState = useSelector((state: RootState) => state.account)
  const referralProjectId = useSelector(
    (state: RootState) => state.referral.projectId
  )

  const loadAccountData = useCallback(async () => {
    dispatch(setLoading(true))
    try {
      const accountData = await AccountService.getAccountData()
      if (accountData) {
        dispatch(setAccountData(accountData))
      } else {
        dispatch(setError("Failed to load account data"))
      }
    } catch (error) {
      dispatch(
        setError(error instanceof Error ? error.message : "Unknown error")
      )
    }
  }, [dispatch])

  const checkAuthentication = useCallback(async () => {
    try {
      const isAuth = await AccountService.isAuthenticated()
      if (!isAuth) {
        dispatch(clearAccount())
      } else if (!accountState.isAuthenticated) {
        await loadAccountData()
      }
    } catch (error) {
      dispatch(clearAccount())
    }
  }, [dispatch, accountState.isAuthenticated, loadAccountData])

  const logout = useCallback(async () => {
    try {
      await AccountService.logout()
    } catch (error) {
      console.error("Logout error:", error)
    }
    dispatch(clearAccount())
  }, [dispatch])

  const saveConfiguration = useCallback(
    async (configuration: ConfigurationData) => {
      try {
        const payload = referralProjectId
          ? { ...configuration, project_id: referralProjectId }
          : configuration
        const result = await ConfigurationService.saveConfiguration(payload)
        if (result && result.configurations) {
          dispatch(setConfigurations(result.configurations))
          return {
            success: true,
            saved_configuration: result.saved_configuration ?? null,
          }
        }
        return { success: false, saved_configuration: null }
      } catch (error) {
        console.error("Saving configuration error:", error)
        return { success: false, saved_configuration: null }
      }
    },
    [dispatch, referralProjectId]
  )

  const editConfiguration = useCallback(
    async (configuration: ConfigurationData) => {
      try {
        const result = await ConfigurationService.editConfiguration(
          configuration
        )
        if (result && result.configurations) {
          dispatch(setConfigurations(result.configurations))
          return true
        }
        return false
      } catch (error) {
        console.error("Editing configuration error:", error)
        return false
      }
    },
    [dispatch]
  )

  const deleteConfiguration = useCallback(
    async (configuration: ConfigurationData) => {
      try {
        const result = await ConfigurationService.deleteConfiguration(
          configuration
        )
        if (result && result.configurations) {
          dispatch(setConfigurations(result.configurations))
          return true
        }
        return false
      } catch (error) {
        console.error("Deleting configuration error:", error)
        return false
      }
    },
    [dispatch]
  )

  return {
    // State
    user: accountState.user,
    configurations: accountState.configurations,
    isLoading: accountState.isLoading,
    isAuthenticated: accountState.isAuthenticated,
    error: accountState.error,

    // Actions
    loadAccountData,
    checkAuthentication,
    logout,
    saveConfiguration,
    editConfiguration,
    deleteConfiguration,

    // Helper getters
    displayName: accountState.user?.display_name || null,
    userRole: accountState.user?.role || null,
    userLanguage: accountState.user?.language || null,
    userId: accountState.user?.my_iko_id || null,
    isAdmin: accountState.user?.role === "iko",
  }
}
